import {
  AtSign,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Star,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { EntityDragRow } from "../../dnd/EntityDragRow";
import { BaseSelectorProps } from "../BaseSelector";

import { TreeView } from "@/components/tree-view";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { CalendarEventEntity } from "@cohesive/api-entities";
import {
  CalendarEventCard,
  CalendarEventCardSkeleton,
} from "@cohesive/ui-components";
import { addDays, format, subDays } from "date-fns";
import { buildGenericById, toGenericArray } from "../../genericProjection";
import {
  Selector,
  SelectorEmpty,
  SelectorResults,
  SelectorSidebar,
} from "../Selector";
import {
  CalendarTabConfig,
  getCalendarIcon,
  getEventsForDay,
  getWeekDaysAround,
  positionEventsInDay,
  TreeDataItem,
} from "./types";
import { useGoogleCalendarEventsList } from "./useGoogleCalendarEventsList";
import { useGoogleCalendarsList } from "./useGoogleCalendarsList";

export const GoogleCalendarSelector: React.FC<BaseSelectorProps> = ({
  connection,
  onSelect,
}) => {
  const [selectedTab, setSelectedTab] = useState<CalendarTabConfig | null>(
    null,
  );
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Fetch calendars
  const { data: calendarsData, isLoading: isCalendarsLoading } =
    useGoogleCalendarsList(connection);

  const calendars = useMemo(() => {
    return calendarsData?.data || [];
  }, [calendarsData?.data]);

  // Build tree data for calendars
  const treeData: TreeDataItem[] = useMemo(() => {
    if (!calendars.length) return [];

    const primaryCalendars: TreeDataItem[] = [];
    const secondaryCalendars: TreeDataItem[] = [];

    calendars.forEach((calendar) => {
      const isPrimary = calendar.data.primary;
      const Icon = getCalendarIcon(isPrimary);

      const item: TreeDataItem = {
        id: calendar.data.id,
        name: calendar.data.title,
        icon: Icon,
        calendarType: isPrimary ? "primary" : "secondary",
        calendarId: calendar.data.id,
        actions: [
          <Button
            key="mention"
            aria-label="Mention calendar in chat"
            variant="ghost"
            size="icon"
            className="!p-2 rounded-full w-5 h-5"
            onClick={(e) => {
              e.stopPropagation();
              // Create a calendar entity
              const calendarEntity = {
                platform: "google-calendar",
                base: "calendar" as const,
                data: calendar.data,
              };
              return onSelect(calendarEntity);
            }}
          >
            <AtSign className="!size-3.5" />
          </Button>,
        ],
        onClick: () => {
          setSelectedTab({
            type: "calendar",
            calendarId: calendar.data.id,
            calendarName: calendar.data.title,
            icon: <Icon className="h-4 w-4" />,
          });
        },
      };

      if (isPrimary) {
        primaryCalendars.push(item);
      } else {
        secondaryCalendars.push(item);
      }
    });

    const result: TreeDataItem[] = [];

    if (primaryCalendars.length > 0) {
      if (primaryCalendars.length === 1) {
        result.push(primaryCalendars[0]);
      } else {
        result.push({
          id: "primary-calendars",
          name: "Primary Calendars",
          icon: Star,
          children: primaryCalendars.sort((a, b) =>
            a.name.localeCompare(b.name),
          ),
        });
      }
    }

    if (secondaryCalendars.length > 0) {
      if (result.length === 0) {
        result.push(
          ...secondaryCalendars.sort((a, b) => a.name.localeCompare(b.name)),
        );
      } else {
        result.push({
          id: "other-calendars",
          name: "Other Calendars",
          icon: Calendar,
          children: secondaryCalendars.sort((a, b) =>
            a.name.localeCompare(b.name),
          ),
        });
      }
    }

    return result;
  }, [calendars, onSelect]);

  // Set initial selected tab (primary calendar if available)
  React.useEffect(() => {
    if (!selectedTab && calendars.length > 0) {
      const primaryCalendar = calendars.find((cal) => cal.data.primary);
      const firstCalendar = primaryCalendar || calendars[0];

      if (firstCalendar) {
        const Icon = getCalendarIcon(firstCalendar.data.primary);
        setSelectedTab({
          type: "calendar",
          calendarId: firstCalendar.data.id,
          calendarName: firstCalendar.data.title,
          icon: <Icon className="h-4 w-4" />,
        });
      }
    }
  }, [calendars, selectedTab]);

  // Fetch events for selected calendar with date range
  const { data: eventsData, isLoading: isEventsLoading } =
    useGoogleCalendarEventsList(connection, {
      calendar_id: selectedTab?.calendarId || "",
      time_min: format(subDays(selectedDate, 7), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
      time_max: format(addDays(selectedDate, 7), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
    });

  // Generic projections (entity_format=both) keyed by event id — placed on the
  // canvas in place of the typed event.
  const genericById = useMemo(
    () =>
      buildGenericById(
        eventsData?.pages.flatMap((page) =>
          toGenericArray(page.data_generic),
        ) ?? [],
      ),
    [eventsData],
  );

  const handleEventSelect = (event: CalendarEventEntity) => {
    onSelect(genericById.get(event.data.id) ?? event);
  };

  // Get all events
  const allEvents = useMemo(() => {
    return eventsData?.pages.flatMap((page) => page.data).filter(Boolean) || [];
  }, [eventsData]);

  // Get week days around selected date
  const weekDays = useMemo(() => {
    return getWeekDaysAround(selectedDate);
  }, [selectedDate]);

  // Get events for selected day
  const dayEvents = useMemo(() => {
    return getEventsForDay(allEvents, selectedDate);
  }, [allEvents, selectedDate]);

  // Position events in day view
  const positionedEvents = useMemo(() => {
    return positionEventsInDay(dayEvents);
  }, [dayEvents]);

  // Separate all-day events from timed events
  const { allDayEvents, timedEvents } = useMemo(() => {
    const allDay = positionedEvents.filter((e) => e.isAllDay);
    const timed = positionedEvents.filter((e) => !e.isAllDay);
    return { allDayEvents: allDay, timedEvents: timed };
  }, [positionedEvents]);

  const calendarEventIds = useMemo(
    () => [...allDayEvents, ...timedEvents].map((pe) => pe.event.data.id),
    [allDayEvents, timedEvents],
  );

  // Day navigation handlers
  const goToPreviousDay = () => setSelectedDate(subDays(selectedDate, 1));
  const goToNextDay = () => setSelectedDate(addDays(selectedDate, 1));
  const goToToday = () => setSelectedDate(new Date());

  const renderDayView = () => {
    if (!selectedTab) {
      return <SelectorEmpty message="Select a calendar to view events" />;
    }

    return (
      <div className="flex flex-col h-full min-h-0">
        {/* Week navigation */}
        <div className="flex items-center justify-center p-2 border-b bg-muted/50 flex-shrink-0">
          <div className="flex items-center gap-1 flex-wrap">
            {weekDays.map((day) => (
              <Button
                key={day.dateKey}
                variant={day.isSelected ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "text-xs px-2 py-1 h-8 min-w-0",
                  day.isToday && !day.isSelected && "ring-1 ring-rust",
                )}
                onClick={() => setSelectedDate(day.date)}
              >
                {day.shortName}
              </Button>
            ))}
          </div>
        </div>

        {/* Day header with navigation */}
        <div className="flex items-center justify-between p-3 border-b flex-shrink-0 min-h-0">
          <Button variant="ghost" size="sm" onClick={goToPreviousDay}>
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="text-center min-w-0 flex-1">
            <div className="font-medium text-sm sm:text-base">
              {format(selectedDate, "EEEE, MMMM d")}
            </div>
            {!weekDays.find((d) => d.isSelected)?.isToday && (
              <Button
                variant="ghost"
                size="sm"
                onClick={goToToday}
                className="text-xs mt-1"
              >
                Go to today
              </Button>
            )}
          </div>

          <Button variant="ghost" size="sm" onClick={goToNextDay}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* All-day events */}
        {isEventsLoading ? (
          <div className="p-2 sm:p-3 border-b bg-muted/25 max-h-24 sm:max-h-32 overflow-y-auto flex-shrink-0">
            <div className="text-xs font-medium text-muted-foreground mb-1 sm:mb-2">
              All day
            </div>
            <div className="space-y-1">
              <CalendarEventCardSkeleton />
            </div>
          </div>
        ) : (
          allDayEvents.length > 0 && (
            <div className="p-2 sm:p-3 border-b bg-muted/25 max-h-24 sm:max-h-32 overflow-y-auto flex-shrink-0">
              <div className="text-xs font-medium text-muted-foreground mb-1 sm:mb-2">
                All day
              </div>
              <div className="space-y-1">
                {allDayEvents.map((positionedEvent) => (
                  <EntityDragRow
                    key={positionedEvent.event.data.id}
                    entity={positionedEvent.event}
                    generic={genericById.get(positionedEvent.event.data.id)}
                    entityId={positionedEvent.event.data.id}
                    orderedIds={calendarEventIds}
                    onSelect={() => handleEventSelect(positionedEvent.event)}
                    dragScope="google-calendar"
                  >
                    <CalendarEventCard
                      id={positionedEvent.event.data.id}
                      title={positionedEvent.event.data.summary}
                      platform="google-calendar"
                      summary={positionedEvent.event.data.summary}
                      start_date_time={
                        positionedEvent.event.data.start_date_time
                      }
                      end_date_time={positionedEvent.event.data.end_date_time}
                      start_time_zone={
                        positionedEvent.event.data.start_time_zone
                      }
                      location={positionedEvent.event.data.location}
                      attendees={positionedEvent.event.data.attendees}
                      status={positionedEvent.event.data.status}
                      html_link={positionedEvent.event.data.html_link}
                      onMentionClick={() =>
                        handleEventSelect(positionedEvent.event)
                      }
                      className=""
                    />
                  </EntityDragRow>
                ))}
              </div>
            </div>
          )
        )}

        {/* Timed events - Fixed scrolling container with better constraints */}
        <div className="flex-1 min-h-0 overflow-hidden relative">
          {isEventsLoading ? (
            <div className="absolute inset-0 overflow-y-auto">
              <div className="p-2 sm:p-3 space-y-1 pb-8">
                {[1, 2, 3].map((i) => (
                  <CalendarEventCardSkeleton key={i} />
                ))}
              </div>
            </div>
          ) : timedEvents.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm px-4">
              No events for this day
            </div>
          ) : (
            <div className="absolute inset-0 overflow-y-auto">
              <div className="p-2 sm:p-3 space-y-1 pb-8">
                {timedEvents.map((positionedEvent) => (
                  <EntityDragRow
                    key={positionedEvent.event.data.id}
                    entity={positionedEvent.event}
                    generic={genericById.get(positionedEvent.event.data.id)}
                    entityId={positionedEvent.event.data.id}
                    orderedIds={calendarEventIds}
                    onSelect={() => handleEventSelect(positionedEvent.event)}
                    dragScope="google-calendar"
                  >
                    <CalendarEventCard
                      id={positionedEvent.event.data.id}
                      title={positionedEvent.event.data.summary}
                      platform="google-calendar"
                      summary={positionedEvent.event.data.summary}
                      start_date_time={
                        positionedEvent.event.data.start_date_time
                      }
                      end_date_time={positionedEvent.event.data.end_date_time}
                      start_time_zone={
                        positionedEvent.event.data.start_time_zone
                      }
                      location={positionedEvent.event.data.location}
                      attendees={positionedEvent.event.data.attendees}
                      status={positionedEvent.event.data.status}
                      html_link={positionedEvent.event.data.html_link}
                      onMentionClick={() =>
                        handleEventSelect(positionedEvent.event)
                      }
                      className=""
                    />
                  </EntityDragRow>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Selector>
      <SelectorSidebar>
        {isCalendarsLoading ? (
          <div className="p-2 space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : (
          <TreeView data={treeData} expandAll={true} />
        )}
      </SelectorSidebar>

      <SelectorResults>{renderDayView()}</SelectorResults>
    </Selector>
  );
};
